/**
 * Что мы поняли из запуска мини-аппа: действительно ли это Telegram и куда
 * вести человека после входа.
 *
 * ПРО ПРИЗНАК TELEGRAM. Раньше признаком считался ещё и сам путь `/tma-init`
 * (`isInTelegram = looksLikeTelegram() || pathname.endsWith('/tma-init')`).
 * Фолбэк достался от времён, когда initData брали только из
 * `window.Telegram.WebApp`, а SDK стоял в <head> и мог не успеть подняться.
 * Сегодня он не даёт ничего: вендорный SDK сам собирает initParams ровно из
 * двух мест — `location.hash` и `sessionStorage.__telegram__initParams`, — а
 * оба уже проверяет looksLikeTelegram(). То есть если синхронных признаков
 * нет, дождавшийся SDK всё равно вернёт пустоту, и «ждать скрипт» нечего.
 *
 * Зато вред фолбэк приносил настоящий: обычный браузер, открывший
 * https://igra.now/tma-init (ссылка из уведомления, репост, история), считался
 * Telegram, получал куку `tma_user=1` на десять лет — и после этого гвард
 * роутера на каждом заходе без токена уводил его с лендинга обратно на
 * /tma-init. Человек не мог попасть на сайт вообще. Поэтому путь признаком
 * больше не является, а уже поставленную куку мы при таком заходе снимаем.
 *
 * ПРО ЦЕЛЬ. Уведомления бота открывают мини-апп кнопкой `web_app` с адресом
 * https://igra.now/tma-init?to=/subscription — контракт с бэкендом. Позже
 * возможна ссылка t.me/igra_now_bot/app?startapp=subscription, и тогда тот же
 * экран приедет в `start_param` внутри initData. Оба источника ведут в один
 * белый список внутренних путей: открытого редиректа на чужой домен из
 * ссылки, которую рассылает бот, быть не должно.
 */
import { looksLikeTelegram } from './telegramSdk'

/**
 * Экраны кабинета, на которые боту разрешено звать.
 *
 * Список закрытый и сравнивается целиком: `//evil.com` и `https://evil.com`
 * в него не попадают по построению, как и любой путь с хвостом.
 */
export const TMA_TARGET_PATHS = [
    '/dashboard',
    '/subscription',
    '/devices',
    '/friends',
    '/payments',
    '/settings',
    '/gifts',
    '/guild',
    '/achievements',
    '/share',
    '/profile',
    // Ссылки бота на /rank ходят с IGRA-109 — не ломаем их.
    '/rank',
] as const

/** Кука, по которой гвард роутера узнаёт «этот браузер пришёл из мини-аппа». */
export const TMA_USER_COOKIE = 'tma_user'

/**
 * FLOW-V4 §2.1 — адрес сессии переноса `/tradein/s/{id}`. Единственный путь с
 * переменной частью, и она сверяется целиком тем же форматом, что у бэкенда
 * (`[A-Za-z0-9_-]`, не длиннее 32): хвоста, запроса и чужих знаков нет.
 */
const TRADEIN_SESSION_PATH = /^\/tradein\/s\/[A-Za-z0-9_-]{1,32}$/

/** Метка мини-аппа `tr_{id}` (`startapp`, ≤ 64 знаков) → адрес сессии. */
const TRADEIN_START_PARAM = /^tr_([A-Za-z0-9_-]{1,32})$/

/** Путь из белого списка либо `null`. Никаких «почти подошло». */
export function sanitizeTmaTargetPath(raw: string | null | undefined): string | null {
    if (typeof raw !== 'string') return null

    const value = raw.trim()

    if (TRADEIN_SESSION_PATH.test(value)) return value

    return (TMA_TARGET_PATHS as readonly string[]).includes(value) ? value : null
}

/**
 * `start_param` читаем прямо из initData: это обычная query-строка, а SDK для
 * неё ждать нельзя — он и не всегда доезжает.
 */
export function readTmaStartParam(initData: string | null | undefined): string | null {
    if (!initData) return null

    try {
        return new URLSearchParams(initData).get('start_param')
    } catch {
        return null
    }
}

/**
 * `subscription` → `/subscription`, `tr_{id}` → `/tradein/s/{id}`; всё, чего
 * нет в списке, — `null`.
 */
function pathFromStartParam(startParam: string | null): string | null {
    if (!startParam) return null

    const session = TRADEIN_START_PARAM.exec(startParam)
    if (session) return `/tradein/s/${session[1]}`

    return sanitizeTmaTargetPath(startParam.startsWith('/') ? startParam : `/${startParam}`)
}

/**
 * Куда вести после входа: `?to=` из ссылки бота, иначе `start_param`, иначе
 * сам адрес запуска (ссылки вида https://igra.now/friends), иначе `null` —
 * «особой цели нет», и вызывающий ведёт в кабинет по умолчанию.
 *
 * Читается ОДИН РАЗ при загрузке, до роутера: гостевой гвард успевает увести
 * с защищённого пути и стереть query.
 */
export function resolveTmaTargetPath(initData: string | null = null): string | null {
    if (typeof window === 'undefined') return null

    let requested: string | null = null
    try {
        requested = new URLSearchParams(window.location.search).get('to')
    } catch {
        // Битый query — просто нет цели.
    }

    return sanitizeTmaTargetPath(requested)
        ?? pathFromStartParam(readTmaStartParam(initData))
        ?? sanitizeTmaTargetPath(window.location.pathname)
}

function hasTmaUserCookie(): boolean {
    return document.cookie.split('; ').some((part) => part === `${TMA_USER_COOKIE}=1`)
}

/** Снять куку `tma_user`, чтобы гвард перестал держать браузер на /tma-init. */
function forgetTmaUser(): void {
    if (!hasTmaUserCookie()) return

    document.cookie = `${TMA_USER_COOKIE}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * Запущены ли мы из Telegram — по настоящим признакам запуска и только по ним.
 *
 * Побочный эффект намеренный и симметричный: настоящий запуск помечает браузер
 * кукой `tma_user` (по ней гвард возвращает человека в мини-апповый вход, если
 * токен протух), а её отсутствие у обычного браузера куку снимает. Без второй
 * половины «отравленные» браузеры так и ходили бы по кругу лендинг →
 * /tma-init.
 */
export function detectTelegramLaunch(): boolean {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false

    const inTelegram = looksLikeTelegram()

    if (inTelegram) {
        if (!hasTmaUserCookie()) document.cookie = `${TMA_USER_COOKIE}=1; path=/; max-age=315360000; SameSite=Lax`
    } else {
        forgetTmaUser()
    }

    return inTelegram
}
